import { Component, OnDestroy, OnInit } from "@angular/core";
import { Page } from "./pages";
import { ToastrService } from "ngx-toastr";
import axios from "axios";
import { environment } from "src/environments/environment";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";

type IntakeSubmitResponse = {
  success: boolean;
  handoff?: {
    registrationId: string;
    createAccountUrl?: string;
    loginUrl?: string;
    existingAccount?: boolean;
    expiresAt: number;
  } | null;
};

@Component({
  selector: "app-form",
  templateUrl: "./form.component.html",
  styleUrls: ["./form.component.css"],
  standalone: false,
})
export class FormComponent implements OnInit, OnDestroy {
  readonly Page = Page;
  readonly showTestingShortcuts = !environment.production;
  private readonly minPage = Page.FIRST_NAME;
  private readonly maxPage = Page.DONE;
  private readonly defaultPage = Page.FIRST_NAME;
  private queryParamSubscription?: Subscription;

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    this.subscribeToUrlStep();
    this.maxDate = new Date().toISOString().split("T")[0];
    this.ensureInitialStepInUrl();
    await this.getClinicians();
  }

  ngOnDestroy() {
    this.queryParamSubscription?.unsubscribe();
  }

  current_page: number = Page.FIRST_NAME;

  maxDate: string = "";

  firstName: string = "";
  lastName: string = "";
  email: string = "";
  email_consent: boolean = false;
  newsletter: boolean = false;
  tn: string = "";
  tn_consent: boolean = false;
  practice: string = "";
  street: string = "";
  city: string = "";
  state: string = "";
  zipCode: string = "";
  care: string = "";
  type_of_care: string = "";
  genetic_testing: boolean = false;
  genetic_testing_method: string = "";
  dob: string = "";
  gender: string = "";
  provider: string = "";
  memberId: string = "";
  relationshipToInsured: string = "";
  cardHolderFirst: string = "";
  cardHolderLast: string = "";
  cardHolderDob: string = "";
  primaryCare: string = "";
  referredBy: string = "";
  pharmacyName: string = "";
  pharmacyStreet: string = "";
  pharmacyCity: string = "";
  pharmacyState: string = "";
  pharmacyZip: string = "";
  pharmacyTn: string = "";
  emergencyContacts: Array<{
    name: string;
    relationship: string;
    phone: string;
  }> = [{ name: "", relationship: "", phone: "" }];
  readonly emergencyContactRelationships: Array<{
    label: string;
    value: string;
  }> = [
    { label: "Spouse", value: "spouse" },
    { label: "Partner", value: "partner" },
    { label: "Parent", value: "parent" },
    { label: "Child", value: "child" },
    { label: "Sibling", value: "sibling" },
    { label: "Friend", value: "friend" },
    { label: "Other", value: "other" },
  ];

  // Option lists backing the PrimeNG p-select controls.
  readonly genderOptions: Array<{ label: string; value: string }> = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "other" },
  ];

  readonly subscriberRelationshipOptions: Array<{
    label: string;
    value: string;
  }> = [
    { label: "Self", value: "self" },
    { label: "Spouse", value: "spouse" },
    { label: "Child", value: "child" },
    { label: "Life Partner", value: "lifepartner" },
    { label: "Other Relationship", value: "other" },
  ];

  readonly insuranceProviders: Array<{ label: string; value: string }> = [
    { label: "Aetna", value: "Aetna" },
    {
      label: "AmeriHealth Administrators",
      value: "AmeriHealth Administrators",
    },
    { label: "Blue Cross Blue Shield", value: "Blue Cross Blue Shield" },
    { label: "Capital Blue Cross", value: "Capital Blue Cross" },
    { label: "Cigna", value: "Cigna" },
    { label: "Highmark Blue Cross", value: "Highmark Blue Cross" },
    { label: "Independence Blue Cross", value: "Independence Blue Cross" },
    { label: "IBC Personal Choice", value: "IBC Personal Choice" },
    {
      label: "Independence Administrators",
      value: "Independence Administrators",
    },
    { label: "Keystone Health Plan East", value: "Keystone Health Plan East" },
    { label: "Meritain Health", value: "Meritain Health" },
    { label: "OptumHealth Behavioral", value: "OptumHealth Behavioral" },
    { label: "Trustmark", value: "Trustmark" },
    { label: "United Healthcare", value: "United Healthcare" },
    { label: "UMR", value: "UMR" },
    { label: "Out of Pocket/Other", value: "Out of Pocket/Other" },
  ];

  readonly usStates: Array<{ label: string; value: string }> = [
    { label: "Alabama", value: "AL" },
    { label: "Alaska", value: "AK" },
    { label: "Arizona", value: "AZ" },
    { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" },
    { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" },
    { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" },
    { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" },
    { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" },
    { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" },
    { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" },
    { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" },
    { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" },
    { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" },
    { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" },
    { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" },
    { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" },
    { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" },
    { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" },
    { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" },
    { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" },
    { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" },
    { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" },
    { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" },
    { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" },
    { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" },
    { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" },
    { label: "Wyoming", value: "WY" },
  ];

  // Choice-card / pill option lists driving the redesigned selection steps.
  readonly specialtyOptions: string[] = [
    "ADD/ADHD",
    "Addiction / Substance Use",
    "Adjustment Disorder",
    "Anger",
    "Anxiety",
    "Dating",
    "Depression",
    "Divorce",
    "Eating Disorder",
    "Fertility",
    "Identity",
    "Intrusive Thoughts",
    "LGBTQIA+",
    "Loneliness",
    "Parenting",
    "Self-Esteem",
    "Stress",
    "Suicidial Feelings",
    "Trauma",
    "Other",
  ];

  readonly genderPreferenceOptions: string[] = [
    "No Preference",
    "Female",
    "Male",
    "Non-binary",
  ];

  readonly ethnicityOptions: string[] = [
    "No Preference",
    "Asian",
    "Black or African American",
    "Hispanic or Latinx",
    "Middle Eastern or North African",
    "Native or Indigenous American",
    "Pacific Islander",
    "White",
  ];

  /** Wizard progress (0-100), used by the header progress bar. Enum order is
   *  monotonic with forward navigation, so this advances smoothly. */
  get progressPercent(): number {
    if (this.current_page >= Page.MATCH) return 100;
    return Math.round((this.current_page / Page.MATCH) * 100);
  }
  specialties: string[] = [];
  genderPreferences: string[] = [];
  ethnicities: string[] = [];
  interface: string = "";
  dayPreference: string = "";
  timePreference: string[] = [];

  emailRegex: RegExp = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  tnRegex: RegExp = /^\+?1?(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  stateRegex: RegExp = /^[A-Z]{2}$/;
  zipRegex: RegExp = /^\d{5}$/;

  selectedClinician: string = "";
  selectedClinicianName: string = "";
  frontImage: string | ArrayBuffer | null = null;
  backImage: string | ArrayBuffer | null = null;
  frontImageName: string | null = null;
  backImageName: string | null = null;

  see_more: boolean = false;
  clinicians: any[] = [];

  matchData: any[] = [];
  isSubmittingFromMatch = false;

  /** Dev-only: optional email override for auto-fill (wins over environment.devAutofillEmail). */
  devAutofillEmailInput = "";

  shakeFrontInput = false;
  shakeBackInput = false;

  disclaimer1: boolean = false;
  disclaimer2: boolean = false;
  disclaimer3: boolean = false;

  submitStatus = "loading";

  onFrontImageSelect(event: any): void {
    const file = event.target.files[0];
    if (file.size > 10000000) {
      alert("File is too large! Must be less than 10MB.");
      this.removeFrontImage();
      return;
    }
    this.previewFrontImage(file);
    this.shakeFrontInput = false;
  }

  onBackImageSelect(event: any): void {
    const file = event.target.files[0];
    if (file.size > 10000000) {
      alert("File is too large! Must be less than 10MB.");
      this.removeBackImage();
      return;
    }
    this.previewBackImage(file);
    this.shakeBackInput = false;
  }

  onDragOver(event: DragEvent, type: "front" | "back"): void {
    event.preventDefault();
    if (type === "front") {
      this.shakeFrontInput = true;
    } else {
      this.shakeBackInput = true;
    }
  }

  onDragLeave(type: "front" | "back"): void {
    if (type === "front") {
      this.shakeFrontInput = false;
    } else {
      this.shakeBackInput = false;
    }
  }

  capitalizeWords(sentence: any): string {
    if (!sentence) return "N/A";
    const words = sentence.split(" ");

    const capitalizedWords = words.map((word: any) => {
      const firstLetter = word.charAt(0).toUpperCase();
      const restOfWord = word.slice(1).toLowerCase();
      return firstLetter + restOfWord;
    });

    return capitalizedWords.join(" ");
  }

  onDrop(event: DragEvent, type: "front" | "back"): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file) {
      if (type === "front") {
        if (file!.size > 10000000) {
          alert("File is too large! Must be less than 10MB.");
          this.removeFrontImage();
          return;
        }
        this.previewFrontImage(file);
        this.shakeFrontInput = false;
      } else {
        if (file!.size > 10000000) {
          alert("File is too large! Must be less than 10MB.");
          this.removeBackImage();
          return;
        }
        this.previewBackImage(file);
        this.shakeBackInput = false;
      }
    }
  }

  previewFrontImage(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.frontImage = reader.result;
      this.frontImageName = file.name;
    };
    reader.readAsDataURL(file);
  }

  previewBackImage(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.backImage = reader.result;
      this.backImageName = file.name;
    };
    reader.readAsDataURL(file);
  }

  removeFrontImage(): void {
    this.frontImage = null;
    this.frontImageName = null;
    let e = <HTMLInputElement>document.getElementById("front");
    if (e) e.value = "";
  }

  removeBackImage(): void {
    this.backImage = null;
    this.backImageName = null;
    let e = <HTMLInputElement>document.getElementById("back");
    if (e) e.value = "";
  }

  async getClinicians() {
    try {
      let resp = await axios.get(
        `${environment.apiUrl}/api/v1/intake/clinicians`,
      );
      if (resp.status != 200) {
        this.toastr.error(
          "We're having some trouble right now. Please try again later.",
        );
        return;
      }
      this.clinicians = resp.data;
    } catch (err) {
      this.toastr.error(
        "We're having some trouble right now. Please try again later.",
      );
      return;
    }
  }

  async match() {
    this.setPage(Page.MATCH);
    try {
      if (this.practice == "va") {
        let matches = [];
        for (let c of this.clinicians) {
          if (c.practice == "va") matches.push(c);
        }
        this.matchData = matches;
        return;
      }
      let resp3 = await axios.post(
        `${environment.apiUrl}/api/v1/intake/match`,
        JSON.stringify({
          ethnicityPreferences: this.ethnicities,
          genderPreferences: this.genderPreferences,
          interface: this.interface,
          dayPreference: this.dayPreference,
          timePreference: this.timePreference,
          specialties: this.specialties,
          practice: this.practice,
        }),
        { headers: { "Content-Type": "application/json" } },
      );
      if (resp3.status != 200 || Object.keys(resp3.data).length === 0) {
        let matches = [];
        for (let c of this.clinicians) {
          matches.push(c);
        }
        this.matchData = matches;
        return;
      }
      let matches = [];
      for (let id of Object.keys(resp3.data)) {
        let i = JSON.parse(
          JSON.stringify(this.clinicians.find((k: any) => k.clinicianId == id)),
        );
        i.score = resp3.data[id];
        matches.push(i);
      }
      for (let c of this.clinicians) {
        let idx = matches.find((k) => k.clinicianId == c.clinicianId);
        if (idx == undefined && c.practice.includes(this.practice)) {
          matches.push(c);
        }
      }
      this.matchData = matches;
    } catch (err) {
      this.toastr.error(
        `We're having trouble matching you to a clinician. Please try again later. Error: ${JSON.stringify(
          err,
        )}`,
      );
    }
  }

  async submit(options: { showDonePage?: boolean } = {}) {
    const showDonePage = options.showDonePage ?? true;
    if (showDonePage) {
      this.setPage(Page.DONE, false, true);
    }
    this.submitStatus = "loading";
    // verify again
    for (let i = Page.FIRST_NAME; i <= Page.MATCH; i++) {
      // if true, its bad -- verify was used for disabled box
      if (this.type_of_care == "therapy") {
        if (i == Page.GENETIC_TESTING) continue;
      } else if (this.type_of_care == "medical") {
        if (i >= Page.INTERFACE && i <= Page.MATCH) continue;
      }
      if (this.verify(i)) {
        this.isSubmittingFromMatch = false;
        this.toastr.error(
          "There are fields missing. Please review the form again.",
        );
        this.setPage(Page.FIRST_NAME, true);
        return;
      }
    }

    try {
      const ins_status =
        this.provider === "Out of Pocket/Other" ? "" : "active";
      // update db
      const resp2 = await axios.post<IntakeSubmitResponse>(
        `${environment.apiUrl}/api/v1/intake/new`,
        JSON.stringify({
          firstName: this.capitalizeWords(this.firstName).trim(),
          lastName: this.capitalizeWords(this.lastName).trim(),
          email: this.email,
          email_consent: this.email_consent,
          newsletter: this.newsletter,
          tn: this.tn,
          tn_consent: this.tn_consent,
          street: this.capitalizeWords(this.street).trim(),
          city: this.capitalizeWords(this.city).trim(),
          state: this.state,
          zipCode: this.zipCode,
          practice: this.practice,
          care: this.care,
          dob: this.dob,
          gender: this.gender,
          provider: this.provider,
          payer: this.provider,
          memberId: this.memberId,
          relationshipToInsured: this.relationshipToInsured,
          primaryCare: this.primaryCare,
          referredBy: this.referredBy,
          pharmacyName: this.capitalizeWords(this.pharmacyName).trim(),
          pharmacyStreet: this.capitalizeWords(this.pharmacyStreet).trim(),
          pharmacyCity: this.capitalizeWords(this.pharmacyCity).trim(),
          pharmacyState: this.pharmacyState.trim().toUpperCase(),
          pharmacyZip: this.pharmacyZip.trim(),
          pharmacyTn: this.pharmacyTn.trim(),
          emergencyContacts: this.emergencyContacts
            .filter((c) => !this.isEmergencyContactRowEmpty(c))
            .map((c) => ({
              name: this.capitalizeWords(c.name).trim(),
              relationship: c.relationship,
              phone: c.phone.trim(),
            })),
          specialties: this.specialties,
          genderPreferences: this.genderPreferences,
          ethnicities: this.ethnicities,
          interface: this.interface,
          subscriber: this.capitalizeWords(
            this.cardHolderFirst + " " + this.cardHolderLast,
          ).trim(),
          subscriberFirst: this.capitalizeWords(this.cardHolderFirst).trim(),
          subscriberLast: this.capitalizeWords(this.cardHolderLast).trim(),
          subscriberDob: this.cardHolderDob,
          type_of_care: this.type_of_care,
          genetic_testing: this.genetic_testing,
          genetic_testing_method: this.genetic_testing_method,
          dayPreference: this.dayPreference,
          timePreference: this.timePreference,
          selectedClinician: this.selectedClinician,
          selectedClinicianName: this.selectedClinicianName,
          frontImage: this.frontImage,
          backImage: this.backImage,
          frontImageName: this.frontImageName,
          backImageName: this.backImageName,
          ins_status: ins_status,
        }),
        { headers: { "Content-Type": "application/json" } },
      );

      if (resp2.status != 200) {
        this.isSubmittingFromMatch = false;
        this.toastr.error("Failed to submit form! Please try again later.");
        this.submitStatus = "error";
        return;
      }

      const handoffUrl = resp2.data?.handoff?.createAccountUrl;
      const loginUrl = resp2.data?.handoff?.loginUrl;

      // Existing account: redirect to login so they can authenticate and link
      if (typeof loginUrl === "string" && loginUrl.length > 0) {
        window.location.assign(loginUrl);
        return;
      }

      // New account: redirect directly to create-account flow
      if (typeof handoffUrl === "string" && handoffUrl.length > 0) {
        window.location.assign(handoffUrl);
        return;
      }

      this.isSubmittingFromMatch = false;
      this.submitStatus = "success";
      if (!showDonePage) {
        this.setPage(Page.DONE, false, true);
      }
    } catch (err) {
      this.isSubmittingFromMatch = false;
      console.log(err);
      this.toastr.error("Failed to submit form! Please try again later.");
      this.submitStatus = "error";
    }
  }

  submitFromMatch(clinicianId: string, clinicianName: string = "") {
    if (this.isSubmittingFromMatch) return;
    this.selectedClinician = clinicianId;
    this.selectedClinicianName = clinicianName;
    this.isSubmittingFromMatch = true;
    void this.submit({ showDonePage: false });
  }

  private async ensureCliniciansLoaded() {
    if (this.clinicians.length > 0) return;
    await this.getClinicians();
  }

  private resolveAutofillEmail(): string {
    const trimmedInput = this.devAutofillEmailInput.trim();
    if (trimmedInput && this.emailRegex.test(trimmedInput)) {
      return trimmedInput;
    }
    const envEmail = (environment.devAutofillEmail ?? "").trim();
    if (envEmail && this.emailRegex.test(envEmail)) {
      return envEmail;
    }
    return `testpatient${Date.now()}@example.com`;
  }

  async autofillForTesting() {
    await this.ensureCliniciansLoaded();

    this.firstName = "Test";
    this.lastName = "Patient";
    this.email = this.resolveAutofillEmail();
    this.email_consent = true;
    this.newsletter = false;
    this.tn = "7328228510";
    this.tn_consent = true;
    this.street = "123 Testing Lane";
    this.city = "Harleysville";
    this.state = "PA";
    this.zipCode = "19438";
    this.dob = "1993-05-15";
    this.gender = "female";
    this.practice = "pa";
    this.care = "individual";
    this.type_of_care = "therapy";
    this.genetic_testing = false;
    this.genetic_testing_method = "";
    this.provider = "Out of Pocket/Other";
    this.relationshipToInsured = "self";
    this.cardHolderFirst = this.firstName;
    this.cardHolderLast = this.lastName;
    this.cardHolderDob = this.dob;
    this.primaryCare = "Primary Care Test 19438";
    this.referredBy = "Website";
    this.pharmacyName = "CVS Pharmacy";
    this.pharmacyStreet = "100 Main St";
    this.pharmacyCity = "Harleysville";
    this.pharmacyState = "PA";
    this.pharmacyZip = "19438";
    this.pharmacyTn = "2155551234";
    this.specialties = ["Anxiety"];
    this.genderPreferences = ["No Preference"];
    this.ethnicities = ["No Preference"];
    this.interface = "Virtual";
    this.dayPreference = "Either";
    this.timePreference = ["Morning"];
    this.disclaimer1 = true;
    this.disclaimer2 = true;
    this.disclaimer3 = true;
    this.frontImage = null;
    this.backImage = null;
    this.frontImageName = null;
    this.backImageName = null;
    this.selectedClinician = "Flex";
    this.selectedClinicianName = "Next Available Clinician";
    this.emergencyContacts = [
      { name: "Jamie Patient", relationship: "spouse", phone: "2155555678" },
      { name: "Riley Patient", relationship: "parent", phone: "2155559012" },
    ];

    this.toastr.success("Test data has been pre-filled.");
  }

  async autofillAndGoToMatchForTesting() {
    await this.autofillForTesting();
    this.matchData =
      this.clinicians.length > 0
        ? [...this.clinicians]
        : [{ name: "Flex Match" }];
    this.setPage(Page.MATCH);
  }

  async autofillAndSubmitForTesting() {
    await this.autofillForTesting();
    this.selectedClinician = "Flex";
    await this.submit();
  }

  nextPage() {
    let nextPage = this.current_page + 1;

    swit: switch (this.current_page) {
      case Page.TYPE_OF_CARE:
        if (this.type_of_care == "therapy") {
          nextPage = Page.INS_DISCLAIMER;
          break swit;
        } else {
          nextPage = this.current_page + 1;
        }
        break;
      case Page.LAST_DETAILS:
        nextPage = this.current_page + 1;
        break;
      case Page.PHARMACY:
        if (this.type_of_care == "medical") {
          void this.submit();
          return;
        } else {
          nextPage = this.current_page + 1;
        }
        break;
      case Page.GENETIC_TESTING:
        if (!this.genetic_testing) {
          nextPage = Page.INS_DISCLAIMER;
        } else {
          nextPage = this.current_page + 1;
        }
        break;
      case Page.INS:
        if (this.provider == "Out of Pocket/Other") {
          nextPage = Page.LAST_DETAILS;
        } else {
          nextPage = this.current_page + 1;
        }
        break;
      case Page.CARE:
        if (this.practice == "va" || this.practice == "philly") {
          nextPage = Page.INS_DISCLAIMER;
        } else {
          nextPage = this.current_page + 1;
        }
        break;
      default:
        nextPage = this.current_page + 1;
    }

    this.setPage(nextPage);
  }

  goBack() {
    let previousPage = this.current_page - 1;

    swit: switch (this.current_page) {
      case Page.INS_DISCLAIMER:
        if (this.type_of_care == "medical") {
          if (!this.genetic_testing) {
            previousPage = Page.GENETIC_TESTING;
            break swit;
          } else {
            previousPage = this.current_page - 1;
          }
        } else if (this.type_of_care == "therapy") {
          if (this.practice == "va" || this.practice == "philly") {
            previousPage = Page.CARE;
          } else {
            previousPage = Page.TYPE_OF_CARE;
          }
        }
        break;

      case Page.LAST_DETAILS:
        if (this.provider == "Out of Pocket/Other") {
          previousPage = Page.INS;
        } else {
          previousPage = this.current_page - 1;
        }
        break swit;
      default:
        previousPage = this.current_page - 1;
    }

    this.setPage(previousPage, true);
  }

  verify(current_page: number) {
    switch (current_page) {
      case Page.FIRST_NAME:
        if (this.firstName.length == 0) return true;
        if (this.firstName.length > 50) return true;
        return false;
      case Page.LAST_NAME:
        if (this.lastName.length == 0) return true;
        if (this.lastName.length > 50) return true;
        return false;
      case Page.EMAIL:
        if (!this.emailRegex.test(this.email)) return true;
        if (!this.email_consent) return true;
        return false;
      case Page.PHONE:
        if (!this.tnRegex.test(this.tn)) return true;
        if (!this.tn_consent) return true;
        return false;
      case Page.ADDR:
        if (this.street.trim().length == 0) return true;
        if (this.city.trim().length == 0) return true;
        if (!this.stateRegex.test(this.state.trim().toUpperCase())) return true;
        if (!this.zipRegex.test(this.zipCode.trim())) return true;
        return false;
      case Page.FUNFACT:
        return false;
      case Page.DOB:
        if (this.dob.length != 10) return true;
        let currDate = new Date();
        let userDate = new Date(this.dob);
        if (userDate > currDate) return true;
        if (userDate < new Date("1900-01-01")) return true;
        return false;
      case Page.GENDER:
        if (
          this.gender != "male" &&
          this.gender != "female" &&
          this.gender != "other"
        )
          return true;
        return false;
      case Page.INS_DISCLAIMER:
        if (!this.disclaimer1 || !this.disclaimer2 || !this.disclaimer3)
          return true;
        return false;
      case Page.INS:
        if (this.provider == "") return true;
        if (this.provider == "Out of Pocket/Other") return false;
        if (
          this.memberId == "" ||
          this.cardHolderFirst == "" ||
          this.cardHolderLast == "" ||
          this.relationshipToInsured == "" ||
          this.cardHolderDob == ""
        )
          return true;
        return false;
      case Page.INS_CARD:
        if (this.provider == "Out of Pocket/Other") return false;
        if (!this.frontImage) return true;
        if (!this.backImage) return true;
        return false;
      case Page.LAST_DETAILS:
        if (this.primaryCare == "") return true;
        return false;
      case Page.PHARMACY:
        if (this.pharmacyName.trim().length === 0) return true;
        if (this.pharmacyStreet.trim().length === 0) return true;
        if (this.pharmacyCity.trim().length === 0) return true;
        if (!this.stateRegex.test(this.pharmacyState.trim().toUpperCase()))
          return true;
        if (!this.zipRegex.test(this.pharmacyZip.trim())) return true;
        if (
          this.pharmacyTn.trim().length > 0 &&
          !this.tnRegex.test(this.pharmacyTn.trim())
        )
          return true;
        return false;
      case Page.EMERGENCY_CONTACTS: {
        const rows = this.emergencyContacts;
        const nonEmpty = rows.filter((c) => !this.isEmergencyContactRowEmpty(c));
        if (nonEmpty.length === 0) return true;
        if (nonEmpty.some((c) => !this.isEmergencyContactRowValid(c))) {
          return true;
        }
        return false;
      }
      case Page.SPECIALTIES:
        if (this.specialties.length == 0) return true;
        return false;
      case Page.GENDER_PREF:
        if (this.genderPreferences.length == 0) return true;
        return false;
      case Page.ETHNICITIY:
        if (this.ethnicities.length == 0) return true;
        return false;
      case Page.INTERFACE:
        if (
          this.interface != "In-Person" &&
          this.interface != "Virtual" &&
          this.interface != "Either"
        )
          return true;
        return false;
      case Page.MATCH:
      default:
        return false;
    }
  }

  addEmergencyContact() {
    this.emergencyContacts.push({ name: "", relationship: "", phone: "" });
  }

  removeEmergencyContact(index: number) {
    this.emergencyContacts.splice(index, 1);
    if (this.emergencyContacts.length === 0) {
      this.emergencyContacts.push({ name: "", relationship: "", phone: "" });
    }
  }

  isEmergencyContactRowValid(contact: {
    name: string;
    relationship: string;
    phone: string;
  }): boolean {
    return (
      contact.name.trim().length >= 2 &&
      this.tnRegex.test(contact.phone.trim())
    );
  }

  isEmergencyContactRowEmpty(contact: {
    name: string;
    relationship: string;
    phone: string;
  }): boolean {
    return (
      contact.name.trim().length === 0 &&
      contact.relationship.trim().length === 0 &&
      contact.phone.trim().length === 0
    );
  }

  updateElement(arr: any[], element: string) {
    if (arr.includes(element)) {
      arr.splice(arr.indexOf(element), 1);
    } else {
      if (
        arr.includes("No Preference") ||
        arr.includes("No Preference / Next Available")
      )
        return;
      if (element == "No Preference" && arr.length > 0) return;
      if (element == "No Preference / Next Available" && arr.length > 0) return;
      arr.push(element);
    }
  }

  updateSelfAttributes() {
    if (this.relationshipToInsured == "self") {
      this.cardHolderFirst = this.firstName;
      this.cardHolderLast = this.lastName;
      this.cardHolderDob = this.dob;
    } else {
      this.cardHolderFirst = "";
      this.cardHolderLast = "";
      this.cardHolderDob = "";
    }
  }

  private ensureInitialStepInUrl() {
    const stepParam = this.route.snapshot.queryParamMap.get("step");
    const parsedStep = Number(stepParam);
    const hasValidStepInUrl =
      stepParam !== null &&
      this.isValidStep(parsedStep) &&
      Number.isInteger(parsedStep);

    if (!hasValidStepInUrl) {
      this.setPage(this.defaultPage);
      return;
    }

    this.setPage(parsedStep, true);
  }

  private subscribeToUrlStep() {
    this.queryParamSubscription = this.route.queryParamMap.subscribe(
      (params) => {
        const stepParam = params.get("step");
        const parsedStep = Number(stepParam);

        if (
          stepParam === null ||
          !Number.isInteger(parsedStep) ||
          !this.isValidStep(parsedStep)
        ) {
          return;
        }

        this.setPage(parsedStep, true);
      },
    );
  }

  private isValidStep(step: number) {
    return step >= this.minPage && step <= this.maxPage;
  }

  private getNextStepForFlow(page: number) {
    switch (page) {
      case Page.TYPE_OF_CARE:
        return this.type_of_care == "therapy" ? Page.INS_DISCLAIMER : page + 1;
      case Page.LAST_DETAILS:
        return page + 1;
      case Page.PHARMACY:
        return this.type_of_care == "medical" ? null : page + 1;
      case Page.GENETIC_TESTING:
        return !this.genetic_testing ? Page.INS_DISCLAIMER : page + 1;
      case Page.INS:
        return this.provider == "Out of Pocket/Other"
          ? Page.LAST_DETAILS
          : page + 1;
      case Page.CARE:
        return this.practice == "va" || this.practice == "philly"
          ? Page.INS_DISCLAIMER
          : page + 1;
      case Page.MATCH:
      case Page.DONE:
        return null;
      default:
        return page + 1 <= this.maxPage ? page + 1 : null;
    }
  }

  private canAdvanceFromStep(page: number) {
    switch (page) {
      case Page.PRACTICE:
        return (
          this.practice == "pa" ||
          this.practice == "philly" ||
          this.practice == "va"
        );
      case Page.CARE:
        return [
          "individual",
          "couple",
          "child",
          "adolescent",
          "family",
        ].includes(this.care);
      case Page.TYPE_OF_CARE:
        return this.type_of_care == "therapy" || this.type_of_care == "medical";
      case Page.GENETIC_TESTING2:
        return (
          this.genetic_testing_method == "ship" ||
          this.genetic_testing_method == "office"
        );
      case Page.DAY_PREF:
        return (
          this.dayPreference == "Weekdays" ||
          this.dayPreference == "Weekends" ||
          this.dayPreference == "Either"
        );
      case Page.TIME_PREF:
        return this.timePreference.length > 0;
      case Page.MATCH:
      case Page.DONE:
        return false;
      default:
        return !this.verify(page);
    }
  }

  private getReachableSteps() {
    const reachableSteps = [this.defaultPage];
    const seen = new Set<number>(reachableSteps);

    let step = this.defaultPage;
    while (this.canAdvanceFromStep(step)) {
      const nextStep = this.getNextStepForFlow(step);
      if (
        nextStep === null ||
        !this.isValidStep(nextStep) ||
        seen.has(nextStep)
      ) {
        break;
      }

      reachableSteps.push(nextStep);
      seen.add(nextStep);
      step = nextStep;
    }

    return reachableSteps;
  }

  private normalizeToAccessibleStep(requestedStep: number) {
    const reachableSteps = this.getReachableSteps();
    const fallbackStep =
      reachableSteps[reachableSteps.length - 1] ?? this.defaultPage;

    if (!this.isValidStep(requestedStep)) {
      return this.defaultPage;
    }

    if (reachableSteps.includes(requestedStep)) {
      return requestedStep;
    }

    const previousReachableStep = [...reachableSteps]
      .reverse()
      .find((step) => step <= requestedStep);
    return previousReachableStep ?? fallbackStep;
  }

  private setPage(
    page: number,
    replaceUrl: boolean = false,
    bypassAccessGuard: boolean = false,
  ) {
    const targetPage = bypassAccessGuard
      ? this.isValidStep(page)
        ? page
        : this.defaultPage
      : this.normalizeToAccessibleStep(page);

    const stepParam = this.route.snapshot.queryParamMap.get("step");
    const currentStepParam =
      stepParam !== null ? Number(stepParam) : Number.NaN;
    const hasCurrentStepParam =
      stepParam !== null && Number.isInteger(currentStepParam);
    const shouldSyncUrl =
      !hasCurrentStepParam || currentStepParam !== targetPage;

    this.current_page = targetPage;

    if (!shouldSyncUrl) return;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: targetPage },
      queryParamsHandling: "merge",
      replaceUrl: replaceUrl || targetPage !== page,
    });
  }
}
